import { useCallback } from "react";
import { useStore } from "react-redux";
import {
  FlightSearchSelectors,
  FlightShopActions,
} from "@b2bportal/core-flights";
import type { AppDispatch } from "@b2bportal/core-types";
import { TripCategory } from "@overrides/types";
import { getIsFixedPointsPricingEligible } from "@/modules/account/selectors";
import type { RootState } from "@/store";
import {
  fetchFlights,
  fetchMulticityFlights,
} from "@/store/flights/shop/store";

interface DepartureFlightsFetchArgs {
  tripCategory: TripCategory | undefined;
  isFixedPointsPricingEligible: boolean | undefined;
}

/**
 * The results-page shop fan-out: a fixed-points shop (when the user is
 * eligible) and a flex shop, multi-city or not. Does not set the loading
 * flag; the caller owns that so the page keeps its timing.
 */
export const fetchDepartureFlights = (
  dispatch: AppDispatch,
  { tripCategory, isFixedPointsPricingEligible }: DepartureFlightsFetchArgs
) => {
  // No search in the store (opt-out confirmed off the results page): there
  // is nothing to re-shop.
  if (tripCategory == null) return;
  const baseParams = { isFixedPointsPricingEligible };
  // An eligible user also gets the flex shop, the fallback when the route
  // is not allowed for fixed pricing.
  const fetchParams = [
    ...(isFixedPointsPricingEligible
      ? [{ ...baseParams, searchFixedPointsFares: true }]
      : []),
    { ...baseParams, searchFixedPointsFares: false },
  ];
  if (tripCategory === TripCategory.MULTI_CITY) {
    fetchParams.forEach((params) => dispatch(fetchMulticityFlights(params)));
  } else {
    fetchParams.forEach((params) => dispatch(fetchFlights(params)));
  }
};

/**
 * Re-runs the results-page shop with the search already in the store. Used
 * after a Grid V2 opt-out: the BFF prices the new shop on V1, and no trip ID
 * from the V2 results survives.
 */
export const useRefetchDepartureFlights = () => {
  const store = useStore<RootState>();

  return useCallback(() => {
    // Read at call time: the opt-out resolves long after this hook rendered.
    const state = store.getState();
    const tripCategory = FlightSearchSelectors.getTripCategory(state);
    // Nothing to re-shop without a search; a pending flag with no fetch
    // behind it would leave the page loading forever.
    if (tripCategory == null) return;
    store.dispatch(FlightShopActions.setFetchFlightsPending());
    fetchDepartureFlights(store.dispatch, {
      tripCategory,
      isFixedPointsPricingEligible: getIsFixedPointsPricingEligible(state),
    });
  }, [store]);
};
